import { z } from 'zod'
import { Cell, Level, Observatory } from './music.ts'
import { SongId } from './song.ts'

export const LabelStyle = z.enum(['roman', 'nashville'])
export type LabelStyle = z.infer<typeof LabelStyle>

export const Profile = z.object({
  labelStyle: LabelStyle,
})
export type Profile = z.infer<typeof Profile>

/** The one night the player has reached (spec §8), and the song order's position per observatory and night. */
export const NightProgress = z.object({
  unlocked: Level,
  /** observatory → night → last offered song id */
  lastOffered: z.record(z.string(), z.record(z.string(), SongId)),
})
export type NightProgress = z.infer<typeof NightProgress>

export const SongStatus = z.enum(['unfinished', 'charted'])
export type SongStatus = z.infer<typeof SongStatus>

/** Exists only for attempted songs; `unheard` = no row. */
export const SongState = z.object({
  songId: SongId,
  status: SongStatus,
  /** Span keys (spec §7) named through in any attempt; the song is charted once every current span's key is here. */
  clearedSpans: z.array(z.string()).default([]),
  chartedObservatory: Observatory.nullable(),
  chartedLevel: Level.nullable(),
})
export type SongState = z.infer<typeof SongState>

export const Counts = z.object({
  correctLive: z.number().int().min(0),
  correctPause: z.number().int().min(0),
  wrong: z.number().int().min(0),
})
export type Counts = z.infer<typeof Counts>

/**
 * The whole player state as one document (spec §10): the app works on it in memory, a store keeps
 * it whole (IndexedDB locally, one JSON row per player on Supabase).
 */
export const PlayerState = z.object({
  playerId: z.string(),
  profile: Profile,
  night: NightProgress,
  /** By song id; only attempted songs have an entry. */
  songs: z.record(z.string(), SongState),
  /** `${observatory}:${cell}` → counts */
  functionStats: z.record(z.string(), Counts),
  /** `${observatory}:${prevCell}>${cell}` → counts */
  transitionStats: z.record(z.string(), Counts),
})
export type PlayerState = z.infer<typeof PlayerState>

export const ErrorReport = z.object({
  id: z.string(),
  songId: SongId,
  annotationVersion: z.number().int().min(1),
  chordIndex: z.number().int().min(0).nullable(),
  note: z.string(),
  status: z.enum(['open', 'resolved', 'rejected']),
  createdAt: z.string(),
})
export type ErrorReport = z.infer<typeof ErrorReport>

/** One answer of an attempt, as applied by `finishAttempt`. */
export const AttemptAnswer = z.object({
  cell: Cell,
  prevCell: Cell.nullable(),
  outcome: z.enum(['live', 'pause', 'wrong']),
})
export type AttemptAnswer = z.infer<typeof AttemptAnswer>

export const AttemptResult = z.object({
  songId: SongId,
  observatory: Observatory,
  level: Level,
  /** Every span key of the song, in song order (spec §7). */
  spans: z.array(z.string()),
  /** The keys of the spans cleared in this run. */
  clearedSpans: z.array(z.string()).default([]),
  answers: z.array(AttemptAnswer),
})
export type AttemptResult = z.infer<typeof AttemptResult>
